import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

/**
 * Interruptor único do app — não existe variante local, e não deve passar a
 * existir.
 *
 * Duas correções que valem pra todas as telas:
 *
 * 1. **Trilho visível nos dois estados.** O desligado era `bg-input`
 *    (`hsl(220 13% 76%)`), ~1,5:1 contra a linha branca — bem abaixo do mínimo
 *    de 3:1 que a WCAG pede pra componente de interface. Numa linha atenuada
 *    (`opacity-40`) caía pra ~1,15:1 e sobrava só o thumb branco com sombra: o
 *    "círculo branco flutuante" que aparecia em Admin > Atividades.
 *    `bg-muted-foreground` dá ~5:1 no tema claro e ~7:1 no escuro.
 *
 * 2. **Alvo de toque de 44px.** O trilho desenhado continua 44×24 (desktop
 *    idêntico), mas o `::before` estende a área clicável do próprio `<button>`
 *    pra 52×44 — o dedo no campo não erra mais.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "peer relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors",
      "before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-['']",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground",
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )}
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
